/**
 * FuelReceiptsTab — Bonuri de motorină cu upload scan în Object Storage.
 *
 * Calcul L/100km = (litri total * 100) / (km_max - km_min) pentru luna selectată.
 */
import { useState, useEffect, useMemo, useRef } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Fuel, Plus, Trash2, Loader2, Upload, FileText, TrendingUp, Calendar, Receipt } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const RO_MONTHS = ["Ianuarie","Februarie","Martie","Aprilie","Mai","Iunie","Iulie","August","Septembrie","Octombrie","Noiembrie","Decembrie"];

export default function FuelReceiptsTab({ companyId, workers = [], vehicles = [] }) {
  const now = new Date();
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [year, setYear] = useState(now.getFullYear());
  const [filterWorker, setFilterWorker] = useState("all");
  const [filterVehicle, setFilterVehicle] = useState("all");
  const [list, setList] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);

  const fetchData = async () => {
    if (!companyId) return;
    setLoading(true);
    try {
      const params = new URLSearchParams({ company_id: companyId, year, month });
      if (filterWorker !== "all") params.append("worker_id", filterWorker);
      if (filterVehicle !== "all") params.append("vehicle_id", filterVehicle);
      const r = await axios.get(`${API}/fuel-receipts?${params.toString()}`);
      setList(r.data.receipts || []);
      setSummary(r.data.summary || null);
    } catch (e) {
      toast.error("Nu am putut încărca bonurile");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); /* eslint-disable-next-line */ }, [companyId, month, year, filterWorker, filterVehicle]);

  const handleDelete = async (id) => {
    if (!window.confirm("Ștergi acest bon?")) return;
    try {
      await axios.delete(`${API}/fuel-receipts/${id}?company_id=${companyId}`);
      toast.success("Bon șters");
      fetchData();
    } catch (e) {
      toast.error("Eroare la ștergere");
    }
  };

  const workerName = (id) => workers.find((w) => w.id === id)?.name || "—";
  const vehicleLabel = (id) => {
    const v = vehicles.find((x) => x.id === id);
    return v ? `${v.plate}${v.name ? ` (${v.name})` : ""}` : "—";
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h3 className="text-lg font-semibold flex items-center gap-2 text-amber-700">
          <Fuel className="w-5 h-5" />
          Bonuri Motorină — {RO_MONTHS[month - 1]} {year}
        </h3>
        <NewReceiptDialog
          companyId={companyId}
          workers={workers}
          vehicles={vehicles}
          open={openDialog}
          onOpenChange={setOpenDialog}
          onSaved={fetchData}
        />
      </div>

      {/* Filtre */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        <Select value={String(month)} onValueChange={(v) => setMonth(Number(v))}>
          <SelectTrigger data-testid="fr-month"><SelectValue /></SelectTrigger>
          <SelectContent>
            {RO_MONTHS.map((m, i) => <SelectItem key={i + 1} value={String(i + 1)}>{m}</SelectItem>)}
          </SelectContent>
        </Select>
        <Input type="number" value={year} onChange={(e) => setYear(Number(e.target.value))} data-testid="fr-year" />
        <Select value={filterWorker} onValueChange={setFilterWorker}>
          <SelectTrigger data-testid="fr-worker"><SelectValue placeholder="Toți șoferii" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Toți șoferii</SelectItem>
            {workers.map((w) => <SelectItem key={w.id} value={w.id}>{w.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filterVehicle} onValueChange={setFilterVehicle}>
          <SelectTrigger data-testid="fr-vehicle"><SelectValue placeholder="Toate vehiculele" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Toate vehiculele</SelectItem>
            {vehicles.map((v) => <SelectItem key={v.id} value={v.id}>{v.plate}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {/* Summary */}
      {summary && summary.count > 0 && (
        <Card className="bg-gradient-to-r from-amber-50 to-orange-50 border-amber-200">
          <CardContent className="pt-6">
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 text-center">
              <div>
                <div className="text-xs text-amber-700 uppercase">Bonuri</div>
                <div className="text-2xl font-bold text-amber-900">{summary.count}</div>
              </div>
              <div>
                <div className="text-xs text-amber-700 uppercase">Litri total</div>
                <div className="text-2xl font-bold text-amber-900">{summary.total_liters} L</div>
              </div>
              <div>
                <div className="text-xs text-amber-700 uppercase">Cheltuit</div>
                <div className="text-2xl font-bold text-amber-900">{summary.total_cost?.toFixed(2)} lei</div>
              </div>
              <div>
                <div className="text-xs text-amber-700 uppercase">KM parcurși</div>
                <div className="text-2xl font-bold text-amber-900">{summary.km_range || "—"}</div>
              </div>
              <div className="col-span-2 sm:col-span-1">
                <div className="text-xs text-amber-700 uppercase flex items-center justify-center gap-1">
                  <TrendingUp className="w-3 h-3" />L/100km
                </div>
                <div className="text-2xl font-bold text-amber-900" data-testid="l-per-100km">
                  {summary.l_per_100km ?? "—"}
                </div>
              </div>
            </div>
            {summary.avg_price_per_liter && (
              <div className="text-xs text-amber-600 text-center mt-3">
                Preț mediu: <strong>{summary.avg_price_per_liter} lei/L</strong>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* List */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm flex items-center gap-2"><Receipt className="w-4 h-4" /> Bonuri în luna selectată</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8 text-slate-500"><Loader2 className="w-6 h-6 animate-spin inline" /></div>
          ) : list.length === 0 ? (
            <div className="text-center py-8 text-slate-400">
              Niciun bon înregistrat pentru luna asta.
              <br />
              <Button size="sm" className="mt-3" onClick={() => setOpenDialog(true)} data-testid="fr-empty-add">
                <Plus className="w-3 h-3 mr-1" /> Adaugă primul bon
              </Button>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="p-2 text-left">Dată</th>
                    <th className="p-2 text-left">Șofer</th>
                    <th className="p-2 text-left">Vehicul</th>
                    <th className="p-2 text-left">Stație</th>
                    <th className="p-2 text-right">Litri</th>
                    <th className="p-2 text-right">Preț/L</th>
                    <th className="p-2 text-right">Total</th>
                    <th className="p-2 text-right">KM</th>
                    <th className="p-2 text-center">Scan</th>
                    <th className="p-2"></th>
                  </tr>
                </thead>
                <tbody>
                  {list.map((r) => (
                    <tr key={r.id} className="border-t hover:bg-slate-50">
                      <td className="p-2 whitespace-nowrap"><Calendar className="w-3 h-3 inline mr-1 text-slate-400" />{r.date}</td>
                      <td className="p-2">{workerName(r.worker_id)}</td>
                      <td className="p-2">{vehicleLabel(r.vehicle_id)}</td>
                      <td className="p-2">{r.station}</td>
                      <td className="p-2 text-right tabular-nums">{r.liters}</td>
                      <td className="p-2 text-right tabular-nums">{r.price_per_liter}</td>
                      <td className="p-2 text-right tabular-nums font-semibold">{r.total_amount} lei</td>
                      <td className="p-2 text-right tabular-nums">{r.km_at_fueling}</td>
                      <td className="p-2 text-center">
                        {r.storage_path ? (
                          <a
                            href={`${API}/fuel-receipts/${r.id}/file?company_id=${companyId}`}
                            target="_blank"
                            rel="noreferrer"
                            className="text-teal-600 hover:underline"
                            data-testid={`fr-view-${r.id}`}
                          >
                            <FileText className="w-4 h-4 inline" />
                          </a>
                        ) : (
                          <span className="text-slate-300">—</span>
                        )}
                      </td>
                      <td className="p-2">
                        <Button size="icon" variant="ghost" onClick={() => handleDelete(r.id)}>
                          <Trash2 className="w-3.5 h-3.5 text-red-500" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}


function NewReceiptDialog({ companyId, workers, vehicles, open, onOpenChange, onSaved }) {
  const today = new Date().toISOString().slice(0, 10);
  const [form, setForm] = useState({
    worker_id: "", vehicle_id: "", date: today, station: "",
    liters: "", price_per_liter: "", total_amount: "", km_at_fueling: "",
  });
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);

  const total = useMemo(() => {
    const l = parseFloat(form.liters);
    const p = parseFloat(form.price_per_liter);
    if (l > 0 && p > 0) return (l * p).toFixed(2);
    return form.total_amount;
  }, [form.liters, form.price_per_liter, form.total_amount]);

  useEffect(() => {
    if (total && total !== form.total_amount) setForm((f) => ({ ...f, total_amount: total }));
    /* eslint-disable-next-line */
  }, [total]);

  const reset = () => {
    setForm({
      worker_id: "", vehicle_id: "", date: today, station: "",
      liters: "", price_per_liter: "", total_amount: "", km_at_fueling: "",
    });
    setFile(null);
    if (fileRef.current) fileRef.current.value = "";
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.worker_id || !form.vehicle_id || !form.liters || !form.price_per_liter || !form.km_at_fueling) {
      return toast.error("Completează toate câmpurile obligatorii");
    }
    setSaving(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, v));
      if (file) fd.append("file", file);
      await axios.post(`${API}/fuel-receipts?company_id=${companyId}`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Bon salvat!");
      reset();
      onOpenChange(false);
      onSaved && onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Eroare la salvare");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogTrigger asChild>
        <Button className="bg-amber-500 hover:bg-amber-600 text-white" data-testid="fr-add-btn">
          <Plus className="w-4 h-4 mr-2" /> Adaugă Bon
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="fr-add-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-amber-700">
            <Fuel className="w-5 h-5" /> Bon motorină nou
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Șofer *</Label>
              <Select value={form.worker_id} onValueChange={(v) => setForm({ ...form, worker_id: v })}>
                <SelectTrigger data-testid="fr-dlg-worker"><SelectValue placeholder="Alege" /></SelectTrigger>
                <SelectContent>{workers.map((w) => <SelectItem key={w.id} value={w.id}>{w.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Vehicul *</Label>
              <Select value={form.vehicle_id} onValueChange={(v) => setForm({ ...form, vehicle_id: v })}>
                <SelectTrigger data-testid="fr-dlg-vehicle"><SelectValue placeholder="Alege" /></SelectTrigger>
                <SelectContent>{vehicles.map((v) => <SelectItem key={v.id} value={v.id}>{v.plate}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Data *</Label>
              <Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} required />
            </div>
            <div>
              <Label>Stație *</Label>
              <Input value={form.station} onChange={(e) => setForm({ ...form, station: e.target.value })} placeholder="Ex: OMV Iași" required />
            </div>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <Label>Litri *</Label>
              <Input type="number" step="0.01" value={form.liters} onChange={(e) => setForm({ ...form, liters: e.target.value })} required data-testid="fr-dlg-liters" />
            </div>
            <div>
              <Label>Preț/L *</Label>
              <Input type="number" step="0.001" value={form.price_per_liter} onChange={(e) => setForm({ ...form, price_per_liter: e.target.value })} required data-testid="fr-dlg-price" />
            </div>
            <div>
              <Label>Total (lei)</Label>
              <Input type="number" step="0.01" value={form.total_amount} onChange={(e) => setForm({ ...form, total_amount: e.target.value })} className="bg-amber-50" />
            </div>
          </div>
          <div>
            <Label>KM la alimentare *</Label>
            <Input type="number" step="1" value={form.km_at_fueling} onChange={(e) => setForm({ ...form, km_at_fueling: e.target.value })} placeholder="ex: 50480" required data-testid="fr-dlg-km" />
            <p className="text-xs text-slate-500 mt-1">Necesar pentru calcul L/100km</p>
          </div>
          <div>
            <Label className="flex items-center gap-1"><Upload className="w-3 h-3" /> Scan bon (opțional, max 5MB)</Label>
            <Input
              ref={fileRef}
              type="file"
              accept=".pdf,.jpg,.jpeg,.png,.webp,.heic,.heif"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
              data-testid="fr-dlg-file"
            />
            {file && <p className="text-xs text-teal-600 mt-1">{file.name} · {(file.size / 1024).toFixed(0)} KB</p>}
          </div>
          <Button type="submit" disabled={saving} className="w-full bg-amber-500 hover:bg-amber-600 text-white" data-testid="fr-dlg-save">
            {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Salvez...</> : "Salvează"}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
